import { DecreasedArrowIcon, IncreasedArrowIcon } from '../../assets/icons';
import { formatPercentages, generateChartData } from '../../helpers';
import Card from '../../components/Card';
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import { TimeConversions } from '../../config/constants';

type AnalyticsSmallCardProps = {
    title: string;
    percentageDifference: string;
    amount: string;
    data?: {
        time: number;
        value: number;
    }[];
    isLoading: boolean;
    showPercentage?: boolean;
};

const AnalyticsSmallCard: React.FC<AnalyticsSmallCardProps> = ({
    title,
    percentageDifference,
    amount,
    data,
    isLoading,
    showPercentage = false,
}) => {
    const hasNonEmptyData = data?.find(({ value }) => value > 0);
    const hasSameValueData = data?.every(({ value }) => data[0].value === value);

    const yesterday = moment().subtract(1, 'day').unix();

    // adding 0.1 ( any small value) to the chart so that linear gradient can display properly without setting gradientUnits="userSpaceOnUse"
    // https://stackoverflow.com/questions/21638169/svg-line-with-gradient-stroke-wont-display-if-vertical-or-horizontal/21639059#21639059
    const emptyData = generateChartData(
        yesterday,
        0,
        TimeConversions.HOURS_PER_DAY,
        TimeConversions.SECONDS_PER_HOUR
    );

    const emptyDataAdjusted = emptyData
        ? [{ ...emptyData[0], value: 0.1 }, ...emptyData.slice(1)]
        : undefined;

    const dataAdjusted =
        hasSameValueData && data
            ? [{ ...data[0], value: data[0].value + 0.1 }, ...data.slice(1)]
            : undefined;

    const isPositiveDifference = +percentageDifference >= 0;

    return (
        <Card className={isPositiveDifference ? 'increased' : 'decreased'}>
            <div className='title'>{title}</div>
            <div className='value'>
                <span className='percentage'>
                    {showPercentage ? (
                        isLoading ? (
                            '-'
                        ) : (
                            <>
                                {isPositiveDifference ? (
                                    <IncreasedArrowIcon />
                                ) : (
                                    <DecreasedArrowIcon />
                                )}
                                {+percentageDifference
                                    ? `${formatPercentages(percentageDifference, 0)}%`
                                    : amount}
                            </>
                        )
                    ) : null}
                </span>
                <span className='amount'>{isLoading ? '-' : amount}</span>
            </div>
            {data && (
                <div className='chart'>
                    <ResponsiveContainer width='100%' height='100%'>
                        <LineChart
                            data={
                                hasNonEmptyData
                                    ? hasSameValueData
                                        ? dataAdjusted
                                        : data
                                    : emptyDataAdjusted
                            }
                            margin={{ top: 0, right: 0, left: 0, bottom: 1 }}
                        >
                            <defs>
                                <linearGradient
                                    id='lineGradientIncrease'
                                    x1='1'
                                    y1='0'
                                    x2='0'
                                    y2='0'
                                >
                                    <stop stopColor='#33CC9E' stopOpacity='1' />
                                    <stop
                                        offset='.884'
                                        stopColor='#33CC9E'
                                        stopOpacity='0.2'
                                    />
                                </linearGradient>
                                <linearGradient
                                    id='lineGradientDecrease'
                                    x1='1'
                                    y1='0'
                                    x2='0'
                                    y2='0'
                                >
                                    <stop stopColor='#FF7675' stopOpacity='1' />
                                    <stop
                                        offset='.884'
                                        stopColor='#A91210'
                                        stopOpacity='0.2'
                                    />
                                </linearGradient>
                            </defs>
                            <XAxis hide dataKey='time' />
                            <YAxis
                                hide
                                dataKey='value'
                                // setting large custom domain when data is "empty" so data appears as a straight line
                                domain={
                                    hasNonEmptyData
                                        ? hasSameValueData
                                            ? ['dataMin - 100', 'dataMax + 100']
                                            : ['dataMin', 'dataMax']
                                        : [-100, 100]
                                }
                            />
                            <Line
                                type='monotone'
                                dot={false}
                                dataKey='value'
                                stroke={`url(#lineGradient${
                                    isPositiveDifference ? 'Increase' : 'Decrease'
                                })`}
                                strokeWidth={2}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Card>
    );
};

export default AnalyticsSmallCard;
